import { Module } from "../../../lib/ui/Module.js";
import { ModalDialog } from "../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../lib/ui/controls/Text.js";
import { TextBox } from "../../../lib/ui/controls/TextBox.js";
import { Button } from "../../../lib/ui/controls/Button.js";

export class WorkspaceBrowser extends Module {

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: 'WorkspaceBrowser',
            moduleTitle: 'Workspace Browser',
        }, options ?? {});

        super(parent, options);

        this.workspaceManager = null;
        this.workspace = null;
        this.workspaces = [];
        this.projects = [];
        this.loadError = null;
        this.isLoading = false;

    }

    getWorkspaceManager() {

        if (!this.workspaceManager)
            this.workspaceManager = this.getServices().getWorkspaceManager();

        return this.workspaceManager;

    }

    getActiveWorkspaceUid(activeRoute = null) {

        return activeRoute?.params?.workspaceUid || this.getApp()?.getActiveRouteParams()?.workspaceUid || null;

    }

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        const workspaceUid = this.getActiveWorkspaceUid(activeRoute);

        if (!workspaceUid) {
            await this.redirectToCurrentWorkspace();
            return;
        }

        await this.reloadWorkspaceProjects(workspaceUid);

    }

    async redirectToCurrentWorkspace() {

        this.isLoading = true;
        this.loadError = null;

        try {
            const dataset = await this.getWorkspaceManager().queryUserWorkspaces();
            const workspaces = Array.isArray(dataset?.items) ? dataset.items : [];

            if (!workspaces.length)
                throw new Error('Workspace aktuálního uživatele nebyl nalezen.');

            if (workspaces.length > 1) {
                this.workspace = null;
                this.projects = [];
                this.workspaces = workspaces;
                return;
            }

            const workspace = workspaces[0];
            if (!(workspace && workspace.uid))
                throw new Error('Workspace nemá vyplněné UID.');

            this.workspace = workspace;
            this.workspaces = workspaces;
            this.getApp().routeTo('workspace-browser', { workspaceUid: workspace.uid });

        } catch (error) {
            this.workspace = null;
            this.workspaces = [];
            this.projects = [];
            this.loadError = true;
            this.handleError(error);
        } finally {
            this.isLoading = false;
            this.invalidate();
        }

    }

    async reloadWorkspaceProjects(workspaceUid = null) {

        workspaceUid = workspaceUid || this.getActiveWorkspaceUid();
        if (!workspaceUid)
            throw new Error('Workspace UID is required.');

        this.isLoading = true;
        this.loadError = null;

        try {
            const dataset = await this.getWorkspaceManager().getAllWorkspaceProjects(workspaceUid);
            this.projects = Array.isArray(dataset?.items) ? dataset.items : [];
            this.workspace = this.resolveWorkspaceFromProjects(workspaceUid, this.projects);
            this.workspaces = [];
        } catch (error) {
            this.workspace = null;
            this.projects = [];
            this.loadError = true;
            this.handleError(error);
        } finally {
            this.isLoading = false;
            this.invalidate();
        }

    }

    resolveWorkspaceFromProjects(workspaceUid, projects) {

        const project = (projects || []).find(item => item?.workspaceUid === workspaceUid) || null;

        return {
            uid: workspaceUid,
            name: project?.workspaceLabel || 'Workspace',
        };

    }

    selectWorkspace(workspace) {

        const workspaceUid = workspace?.uid || null;
        if (!workspaceUid)
            return;

        this.getApp().routeTo('workspace-browser', { workspaceUid });

    }

    openProject(project) {

        const workspaceUid = this.getActiveWorkspaceUid();
        const projectUid = project?.projectUid || null;

        if (!workspaceUid || !projectUid)
            return;

        this.getApp().routeTo('workspace-project-editor', { workspaceUid, projectUid });

    }

    createProject() {

        const workspaceUid = this.getActiveWorkspaceUid();
        if (!workspaceUid)
            return;

        const app = this.getApp();
        const browser = this;
        const data = {
            name: '',
            description: '',
        };

        const nameTextBox = new TextBox(null, {
            autofocus: true,
            value: () => data.name,
            onValueChange: (value) => { data.name = value; },
        });

        const descriptionTextBox = new TextBox(null, {
            multiline: true,
            value: () => data.description,
            onValueChange: (value) => { data.description = value; },
        });

        const modal = new ModalDialog(null, {
            title: 'Nový projekt',
            controls: [
                new Text(null, { text: 'Název projektu' }),
                nameTextBox,
                new Text(null, { text: 'Popis' }),
                descriptionTextBox,
            ],
            buttons: [
                new Button(null, {
                    label: 'Vytvořit',
                    icon: '#add',
                    onClick: createConfirmed,
                }),
                new Button(null, {
                    label: 'Zrušit',
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => modal.hide(),
                }),
            ],
        });

        modal.show(app);

        async function createConfirmed() {

            const name = data.name?.trim();
            if (!name) {
                browser.handleError('Název projektu je povinný.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                const project = await browser.getWorkspaceManager().createWorkspaceProject(workspaceUid, {
                    name,
                    description: data.description,
                });
                await browser.reloadWorkspaceProjects(workspaceUid);
                browser.openProject(project);
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        }

    }

    deleteProject(project) {

        const workspaceUid = this.getActiveWorkspaceUid();
        const projectUid = project?.projectUid || null;

        if (!workspaceUid || !projectUid)
            return;

        const app = this.getApp();
        const browser = this;
        const projectName = project.projectName || project.projectLabel || project.projectUid;

        const modal = new ModalDialog(null, {
            title: 'Smazat projekt',
            controls: [
                new Text(null, { text: `Smazat projekt "${projectName}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: 'Smazat',
                    icon: '#delete',
                    onClick: deleteConfirmed,
                }),
                new Button(null, {
                    label: 'Zrušit',
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => modal.hide(),
                }),
            ],
        });

        modal.show(app);

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getWorkspaceManager().deleteWorkspaceProject(workspaceUid, projectUid);
                await browser.reloadWorkspaceProjects(workspaceUid);
            } catch (error) {
                browser.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        }

    }

    _render(r, vm) {

        const browser = this;

        return r.module(null, [
            r.header(null, [
                r.h4([browser.workspace?.name || (browser.getActiveWorkspaceUid() ? 'Workspace' : 'Výběr workspace')]),
                r.nav(null, [
                    r.iconButton('#refresh', {
                        css: 'circle transparent',
                        props: {
                            type: 'button',
                            disabled: browser.isLoading,
                            onclick: () => browser.getActiveWorkspaceUid()
                                ? browser.reloadWorkspaceProjects()
                                : browser.redirectToCurrentWorkspace(),
                        },
                    }),
                    r.button('Nový projekt', '#add', {
                        props: {
                            type: 'button',
                            disabled: browser.isLoading || !browser.getActiveWorkspaceUid(),
                            onclick: () => browser.createProject(),
                        },
                    }),
                ]),
            ]),
            r.section(null, [
                renderContent(),
            ]),
        ]);

        function renderContent() {

            if (browser.isLoading)
                return r.p(['Načítám workspace...']);

            if (browser.loadError)
                return r.p('error', ['Workspace se nepodařilo načíst.']);

            if (!browser.getActiveWorkspaceUid())
                return renderWorkspaceSelection();

            if (!browser.projects.length)
                return r.p(['Workspace zatím neobsahuje žádné projekty.']);

            return r.table({ css: 'lines hover' }, [
                r.thead([
                    r.trow([
                        r.thcell(['Projekt']),
                        r.thcell('min', ['Role']),
                        r.thcell('min', ['Akce']),
                    ]),
                ]),
                r.tbody(browser.projects.map(project => r.trow([
                    r.tcell([
                        r.span({
                            css: 'pointer-cursor', props: {
                                onclick: () => browser.openProject(project),
                            }
                        }, [
                            project.projectName || project.projectLabel || project.projectUid || 'Projekt'
                        ]),
                    ]),
                    r.tcell('min', [project.userRoleLabel || project.userRoleCode || '']),
                    r.tcell('min', [
                        r.iconButton('#open', {
                            css: 'circle transparent',
                            props: { type: 'button', disabled: !project.projectUid, onclick: () => browser.openProject(project) },
                        }),
                        r.iconButton('#delete', {
                            css: 'circle transparent',
                            props: { type: 'button', disabled: !project.projectUid, onclick: () => browser.deleteProject(project) },
                        }),
                    ]),
                ]))),
            ]);

        }

        function renderWorkspaceSelection() {

            if (!browser.workspaces.length)
                return r.p(['Přesměrovávám na workspace...']);

            return r.table({ css: 'lines hover' }, [
                r.thead([
                    r.trow([
                        r.thcell(['Workspace']),
                        r.thcell(['Vlastník']),
                        r.thcell('min', ['Akce']),
                    ]),
                ]),
                r.tbody(browser.workspaces.map(workspace => r.trow([
                    r.tcell([
                        r.span({
                            css: 'pointer-cursor',
                            props: {
                                onclick: () => browser.selectWorkspace(workspace),
                            }
                        }, [
                            getWorkspaceLabel(workspace)
                        ]),
                    ]),
                    r.tcell([workspace.ownerLabel || '']),
                    r.tcell('min', [
                        r.iconButton('#open', {
                            css: 'circle transparent',
                            props: { type: 'button', disabled: !workspace.uid, onclick: () => browser.selectWorkspace(workspace) },
                        }),
                    ]),
                ]))),
            ]);

        }

        function getWorkspaceLabel(workspace) {

            return workspace?.displayLabel || workspace?.name || workspace?.uid || 'Workspace';

        }

    }

}
